import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProjectForm } from '../../context/ProjectFormContext';
import { sendSurveyLead, sendThankYouEmail } from '../../services/projectSubmission';
import {
  SERVICES,
  TECHNOLOGY_CATEGORIES,
  BUDGET_OPTIONS,
  TIMELINE_OPTIONS,
  PLATFORMS,
} from '../../constants/projectFormConfig';
import { FiX, FiArrowRight, FiArrowLeft, FiSearch, FiCheck, FiArrowDown } from 'react-icons/fi';

/**
 * ProjectSurvey
 * Second page of chain - detailed project information (compact survey style)
 * All fields are optional - user can skip
 */
const ProjectSurvey = () => {
  const navigate = useNavigate();
  const { formData, updateFormData, toggleArrayField, addTechnology, removeTechnology, leadId } = useProjectForm();
  
  // Track current step (0-5)
  const [currentStep, setCurrentStep] = useState(0);
  const [arrowBouncing, setArrowBouncing] = useState(true);
  const [techSearch, setTechSearch] = useState('');
  const [isSearchFocused, setIsSearchFocused] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setArrowBouncing(false), 2000);
    return () => clearTimeout(timer);
  }, []);
  const totalSteps = 5;
  const progress = ((currentStep + 1) / totalSteps) * 100;

  // Flatten all technologies for search
  const allTechnologies = useMemo(() => {
    return Object.entries(TECHNOLOGY_CATEGORIES).flatMap(([category, techs]) =>
      techs.map((tech) => ({ tech, category }))
    );
  }, []);

  // Filter technologies based on search
  const filteredTechnologies = useMemo(() => {
    if (!techSearch.trim()) return [];
    const search = techSearch.toLowerCase();
    return allTechnologies.filter(({ tech }) =>
      tech.toLowerCase().includes(search)
    );
  }, [techSearch, allTechnologies]);

  const handleSkip = () => {
    if (currentStep < totalSteps - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleFinish();
    }
  };

  const handleNext = () => {
    if (currentStep < totalSteps - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleFinish();
    }
  };

  const handleFinish = async () => {
    try {
      await sendSurveyLead(formData, leadId);
    } catch (error) {
      console.error('Error sending survey emails:', error);
    }
    navigate('/thanks');
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const toggleTech = (tech) => {
    if (formData.technologies.includes(tech)) {
      removeTechnology(tech);
    } else {
      addTechnology(tech);
    }
  };

  return (
    <div className="min-h-screen bg-[#fefdfb] flex flex-col relative">
      {/* Decorative corners */}
      <div className="corner corner-top-left"></div>
      <div className="corner corner-top-right"></div>
      <div className="corner corner-bottom-left"></div>
      <div className="corner corner-bottom-right"></div>
      
      {/* Dot grid background */}
      <div 
        className="absolute inset-0 pointer-events-none z-0" 
        style={{
          backgroundImage: 'radial-gradient(circle, rgb(226, 232, 240) 1.5px, transparent 1.5px)',
          backgroundSize: '24px 24px'
        }}
      ></div>

      {/* Header with Logo and Skip */}
      <div className="sticky top-0 z-50 bg-[#fefdfb]">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-3 flex items-center justify-between h-16">
          <div 
            className="cursor-pointer group"
            onClick={() => navigate('/')}
          >
            <span className="text-3xl md:text-5xl font-bold tracking-tight text-black group-hover:opacity-75 transition-opacity">
              .BERCASTLE
            </span>
          </div>
          
          <div className="flex items-center gap-4">
            <span className="text-sm font-medium text-slate-600">
              Step {currentStep + 1} of {totalSteps}
            </span>
            <button
              onClick={handleSkip}
              className="flex items-center gap-2 px-3 py-2 text-slate-900 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors font-medium text-sm"
            >
              Skip
              <FiArrowRight size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 w-full max-w-2xl mx-auto px-4 md:px-8 pb-32 pt-24 overflow-x-hidden relative z-10">
        {/* Progress Bar */}
        <div className="h-1 bg-slate-100 rounded-full overflow-hidden mb-8">
          <div
            className="h-full bg-[#d6fa70] transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
        {/* Step 1: Services */}
        {currentStep === 0 && (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2">
                Which services interest you?
              </h2>
              <p className="text-slate-600">Select all that apply</p>
            </div>

            <div className="flex flex-wrap gap-2">
              {SERVICES.map((service) => (
                <button
                  key={service.id}
                  onClick={() => toggleArrayField('services', service.label)}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                    formData.services.includes(service.label)
                      ? 'bg-slate-900 text-[#d6fa70]'
                      : 'bg-slate-100 text-slate-900 hover:bg-slate-200'
                  }`}
                >
                  {formData.services.includes(service.label) && <FiCheck size={16} />}
                  {service.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 2: Technologies */}
        {currentStep === 1 && (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2">
                What technologies do you need?
              </h2>
              <p className="text-slate-600">Search and select from the list</p>
            </div>

            {/* Selected Technologies */}
            {formData.technologies.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-slate-700 mb-2">Selected</h3>
                <div className="flex flex-wrap gap-2">
                  {formData.technologies.map((tech) => (
                    <button
                      key={tech}
                      onClick={() => toggleTech(tech)}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-900 text-[#d6fa70] text-sm font-medium hover:bg-slate-800 transition-all"
                    >
                      {tech}
                      <FiX size={14} />
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Search/Select Input */}
            <div className="relative">
              <div className="relative">
                <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
                <input
                  type="text"
                  value={techSearch}
                  onChange={(e) => setTechSearch(e.target.value)}
                  onFocus={() => setIsSearchFocused(true)}
                  onBlur={() => setTimeout(() => setIsSearchFocused(false), 200)}
                  placeholder="Click to select or search technologies..."
                  className="w-full pl-10 pr-4 py-3 border-2 border-slate-200 rounded-lg focus:border-slate-900 focus:outline-none transition-colors cursor-pointer"
                />
              </div>

              {/* Dropdown List */}
              {isSearchFocused && (
                <div className="absolute z-10 w-full mt-2 bg-white border-2 border-slate-900 rounded-lg shadow-xl max-h-80 overflow-y-auto">
                  {/* Add custom technology option */}
                  {techSearch.trim() && !allTechnologies.some(({ tech }) => tech.toLowerCase() === techSearch.toLowerCase()) && (
                    <button
                      onMouseDown={(e) => {
                        e.preventDefault();
                        const customTech = techSearch.trim();
                        if (customTech && !formData.technologies.includes(customTech)) {
                          addTechnology(customTech);
                        }
                        setTechSearch('');
                      }}
                      className="w-full px-4 py-3 text-left bg-[#d6fa70] hover:bg-[#c5e960] transition-colors border-b-2 border-slate-900 font-medium text-slate-900"
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-lg">+</span>
                        <span>Add "{techSearch.trim()}"</span>
                      </div>
                    </button>
                  )}
                  
                  {/* Filtered or all technologies */}
                  {(filteredTechnologies.length > 0 ? filteredTechnologies : allTechnologies).map(({ tech, category }) => (
                    <button
                      key={tech}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        toggleTech(tech);
                      }}
                      className="w-full px-4 py-2.5 text-left hover:bg-slate-50 transition-colors flex items-center justify-between border-b border-slate-100 last:border-b-0"
                    >
                      <div className="flex-1">
                        <div className="font-medium text-slate-900">{tech}</div>
                        <div className="text-xs text-slate-500">{category}</div>
                      </div>
                      {formData.technologies.includes(tech) && (
                        <FiCheck className="text-[#d6fa70] bg-slate-900 rounded-full p-1 flex-shrink-0" size={20} />
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Step 3: Budget */}
        {currentStep === 2 && (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2">
                What's your budget?
              </h2>
              <p className="text-slate-600">Select your budget range</p>
            </div>

            <div className="flex flex-wrap gap-2">
              {BUDGET_OPTIONS.map((option) => (
                <button
                  key={option}
                  onClick={() => updateFormData('budget', option)}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                    formData.budget === option
                      ? 'bg-slate-900 text-[#d6fa70]'
                      : 'bg-slate-100 text-slate-900 hover:bg-slate-200'
                  }`}
                >
                  {option}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 4: Timeline */}
        {currentStep === 3 && (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2">
                What's your timeline?
              </h2>
              <p className="text-slate-600">When do you need this done?</p>
            </div>

            <div className="flex flex-wrap gap-2">
              {TIMELINE_OPTIONS.map((option) => (
                <button
                  key={option}
                  onClick={() => updateFormData('timeline', option)}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                    formData.timeline === option
                      ? 'bg-slate-900 text-[#d6fa70]'
                      : 'bg-slate-100 text-slate-900 hover:bg-slate-200'
                  }`}
                >
                  {option}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 5: Platforms */}
        {currentStep === 4 && (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2">
                Target platforms?
              </h2>
              <p className="text-slate-600">Select all platforms that apply</p>
            </div>

            <div className="flex flex-wrap gap-2">
              {PLATFORMS.map((platform) => (
                <button
                  key={platform}
                  onClick={() => toggleArrayField('platforms', platform)}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                    formData.platforms.includes(platform)
                      ? 'bg-slate-900 text-[#d6fa70]'
                      : 'bg-slate-100 text-slate-900 hover:bg-slate-200'
                  }`}
                >
                  {formData.platforms.includes(platform) && <FiCheck size={16} />}
                  {platform}
                </button>
              ))}
            </div>
          </div>
        )}

      </div>

      {/* Fixed Navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-[#fefdfb] border-t border-slate-200 z-50">
        {/* Bouncing arrow hint on step 1 */}
        {currentStep === 0 && arrowBouncing && (
          <div className="absolute -top-10 right-0 left-0 flex justify-end pr-6 md:pr-[max(1.5rem,calc(50%-672px/2+1.5rem))] pointer-events-none">
            <div className="animate-bounce text-slate-900">
              <FiArrowDown size={28} strokeWidth={2.5} />
            </div>
          </div>
        )}
        <div className="max-w-2xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
          <button
            onClick={handleBack}
            disabled={currentStep === 0}
            className={`flex items-center gap-2 px-6 py-2 rounded-full font-medium transition-all ${
              currentStep === 0
                ? 'text-slate-300 cursor-not-allowed'
                : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100'
            }`}
          >
            <FiArrowLeft size={18} />
            Back
          </button>

          <div className="text-sm text-slate-600">
            {currentStep + 1} / {totalSteps}
          </div>

          <button
            onClick={handleNext}
            className="flex items-center gap-2 px-6 py-2 rounded-full bg-slate-900 text-[#d6fa70] font-medium hover:bg-slate-800 transition-all"
          >
            {currentStep === totalSteps - 1 ? 'Finish' : 'Next'}
            <FiArrowRight size={18} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProjectSurvey;
